import * as React from 'react';
import { Autocomplete, Chip, Stack } from '@mui/material';
import { TextInput } from '@coverright/ui/inputs';
import { getUserName } from '@coverright/utils';
import { AgentOutput } from '@coverright/data-access/types/enrollment';
import { useAgentsQuery } from '../../../shared';

interface AttendeeSelectProps {
  value: string[];
  onChange: (emails: string[]) => void;
}

export const AttendeeSelect = ({ value, onChange }: AttendeeSelectProps) => {
  const [inputValue, setInputValue] = React.useState('');

  const { data, loading } = useAgentsQuery({
    variables: { filter: { enabled: null }, page: { page: 0, size: 1000 } },
  });

  const agentOptions: AgentOutput[] = data?.agents.data ?? [];

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !value.includes(email)) {
      onChange([...value, email]);
    }
  };

  return (
    <Stack gap={1}>
      <Autocomplete
        freeSolo
        fullWidth
        loading={loading}
        options={agentOptions}
        autoHighlight
        clearOnBlur
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionLabel={(o) => (typeof o === 'string' ? o : getUserName(o))}
        inputValue={inputValue}
        onInputChange={(_, v, reason) => {
          if (reason !== 'reset') setInputValue(v);
        }}
        onChange={(_, v) => {
          const email =
            typeof v === 'string' ? v : (v as AgentOutput)?.email ?? '';
          addEmail(email);
          setInputValue('');
        }}
        renderInput={(params) => (
          <TextInput
            {...params}
            loading={loading}
            hideTick
            label="Add attendees"
            placeholder="Enter agent name or email"
            value={inputValue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addEmail(inputValue);
                setInputValue('');
              }
            }}
          />
        )}
      />
      {value.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {value.map((email) => {
            const agent = agentOptions.find((a) => a.email === email);
            return (
              <Chip
                key={email}
                size="small"
                label={agent ? getUserName(agent) : email}
                onDelete={() => onChange(value.filter((e) => e !== email))}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
